import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';
import { env } from '../../../environments/env';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { OfflineQueueService } from './offline-queue.service';

export interface AnnotationBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Four normalized (0..1) corner coordinates — preserves rotation/perspective. */
export interface QuadAnnotation {
  tl: { x: number; y: number };
  tr: { x: number; y: number };
  br: { x: number; y: number };
  bl: { x: number; y: number };
}

export interface SubmissionPayload {
  /** Base64 JPEG image of the frame. */
  image: string;
  /** Bounding boxes (normalized 0..1). */
  detections: {
    bbox: AnnotationBox;
    confidence: number;
    manualText?: string;
    /** 4-corner quad (normalized 0..1) — sent when the user draws a free quad. */
    quad?: QuadAnnotation;
    category?: string;
  }[];
  /** Model version that produced the detections (empty if manual). */
  modelVersion: string;
  /** ISO timestamp. */
  timestamp: string;
  projectId?: string;
}

/**
 * Sends detection results and training annotations to the backend.
 * Falls back to the offline queue when the network is unavailable.
 */
@Injectable({ providedIn: 'root' })
export class TagPlateAnnotationService {
  private projectId: number | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<State>,
    private readonly offlineQueue: OfflineQueueService,
  ) {}

  /** Resolve the current project ID from the store (cached after first call). */
  private async resolveProjectId(): Promise<number | null> {
    if (this.projectId) return this.projectId;
    const project = await this.store.select(getCurrentProject).pipe(first()).toPromise();
    this.projectId = project?.id ?? null;
    return this.projectId;
  }

  // ── Submit detection / annotation ──────────────────────────────────────

  /** Submit detection results (AI or manual) to the backend. Drops silently on failure. */
  submit(payload: SubmissionPayload): Observable<void> {
    return new Observable<void>((observer) => {
      this.resolveProjectId().then((projectId) => {
        if (!projectId) {
          console.warn('[Annotation] No project ID – cannot submit');
          observer.complete();
          return;
        }
        const url = `${env.get().api.baseUrl}/projects/${projectId}/tag/detections`;
        console.log(`[Annotation] POST ${url}`, { detections: payload.detections.length });
        this.http
          .post<void>(url, payload)
          .pipe(
            catchError((err) => {
              console.warn('[Annotation] POST failed, dropping', err.status);
              return of(undefined as void);
            }),
          )
          .subscribe(observer);
      });
    });
  }

  // ── Offline sync ────────────────────────────────────────────────────────

  /** Attempt to flush the offline queue. Call on reconnect. */
  async syncOfflineQueue(): Promise<{ synced: number; failed: number }> {
    const pending = await this.offlineQueue.getAll();
    let synced = 0;
    let failed = 0;

    for (const item of pending) {
      try {
        await this.http.post<void>(item.url, item.payload).toPromise();
        await this.offlineQueue.remove(item.id!);
        synced++;
      } catch {
        await this.offlineQueue.incrementRetries(item.id!);
        failed++;
      }
    }

    if (synced > 0) {
      console.log(`[Annotation] Synced ${synced} queued item(s), ${failed} failed`);
    }
    return { synced, failed };
  }

  // ── Utilities ───────────────────────────────────────────────────────────

  canvasToBase64(canvas: HTMLCanvasElement, quality = 0.85): string {
    return canvas.toDataURL('image/jpeg', quality).replace(/^data:image\/jpeg;base64,/, '');
  }

  normalizeBoxes(boxes: { x0: number; y0: number; x1: number; y1: number }[], imageWidth: number, imageHeight: number): AnnotationBox[] {
    return boxes.map((b) => ({
      x: b.x0 / imageWidth,
      y: b.y0 / imageHeight,
      width: (b.x1 - b.x0) / imageWidth,
      height: (b.y1 - b.y0) / imageHeight,
    }));
  }

  /** Normalize a DrawnQuad's 4 corners to 0..1 range. */
  normalizeQuad(
    quad: { tl: { x: number; y: number }; tr: { x: number; y: number }; br: { x: number; y: number }; bl: { x: number; y: number } },
    imageWidth: number,
    imageHeight: number,
  ): QuadAnnotation {
    const norm = (pt: { x: number; y: number }) => ({
      x: pt.x / imageWidth,
      y: pt.y / imageHeight,
    });
    return { tl: norm(quad.tl), tr: norm(quad.tr), br: norm(quad.br), bl: norm(quad.bl) };
  }
}
